import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CourseSurvey() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const courseId = searchParams.get('courseId');
  const courseTitle = searchParams.get('courseTitle') || 'Course';
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [certificateId, setCertificateId] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificatePdfUrl, setCertificatePdfUrl] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    q1_email: '',
    q2_profession: '',
    q2_profession_other: '',
    q3_years_practicing: '',
    q4_practice_type: '',
    q4_practice_type_other: '',
    q5_course_objectives: null,
    q6_instructor_knowledge: null,
    q7_material_quality: null,
    q8_relevance: null,
    q9_understanding_improvement: null,
    q10_presentation_effectiveness: null,
    q11_dentoalveolar_surgery: false,
    q11_third_molar_surgery: false,
    q11_oral_pathology: false,
    q11_dental_implants: false,
    q11_surgical_complications: false,
    q11_bone_grafting: false,
    q11_sedation_anesthesia: false,
    q12_content_level: '',
    q13_preferred_day: '',
    q14_preferred_time: '',
    q15_learning_format: '',
    q16_networking_interest: '',
    q17_cost_factor: '',
    q18_team_training_interest: '',
    q19_most_valuable_aspect: '',
    q20_suggested_changes: '',
    q21_other_feedback: ''
  });

  useEffect(() => {
    if (!courseId) {
      toast.error('Course ID is required');
      navigate('/dashboard');
      return;
    }

    // Check if already submitted
    checkIfSubmitted();
    
    // Pre-fill email from user profile if available
    const token = localStorage.getItem('session_token');
    if (token) {
      fetch(`${API}/api/current-user`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          if (res.ok) {
            return res.json();
          }
          return null;
        })
        .then(user => {
          if (user && user.email) {
            setFormData(prev => ({ ...prev, q1_email: user.email }));
          }
        })
        .catch(err => console.error('Error fetching user:', err));
    }
  }, [courseId, navigate]);

  const checkIfSubmitted = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API}/api/course-survey/${courseId}/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        if (data.submitted) {
          setSubmitted(true);
        }
      }
    } catch (error) {
      console.error('Error checking survey status:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const loadCertificate = async () => {
    try {
      const token = localStorage.getItem('session_token');
      console.log('Fetching certificate for course:', courseId);
      
      const response = await fetch(`${API}/courses/${courseId}/certificate/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        console.error('Certificate fetch failed:', response.status, response.statusText);
        const text = await response.text();
        console.error('Response body:', text);
        throw new Error(`Failed to load certificate: ${response.status}`);
      }
      
      // Create blob URL from response
      const blob = await response.blob();
      console.log('Certificate blob created:', blob.size, 'bytes', blob.type);
      
      // Check if blob is actually a PDF
      if (!blob.type.includes('pdf')) {
        console.error('Response is not a PDF:', blob.type);
        const text = await blob.text();
        console.error('Blob content:', text);
        throw new Error('Invalid certificate format');
      }
      
      // Convert blob to base64 data URL for iframe
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        console.log('Certificate data URL created, length:', dataUrl.length);
        setCertificatePdfUrl(dataUrl);
        setShowCertificate(true);
      };
      reader.onerror = (e) => {
        console.error('FileReader error:', e);
        toast.error('Failed to load certificate');
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error loading certificate:', error);
      toast.error('Failed to load certificate. You can access it from your dashboard.');
    }
  };

  const validateForm = () => {
    // Required fields
    const requiredFields = [
      'q1_email', 'q2_profession', 'q3_years_practicing', 'q4_practice_type',
      'q5_course_objectives', 'q6_instructor_knowledge', 'q7_material_quality',
      'q8_relevance', 'q9_understanding_improvement', 'q10_presentation_effectiveness',
      'q12_content_level', 'q13_preferred_day', 'q14_preferred_time', 'q15_learning_format',
      'q16_networking_interest', 'q17_cost_factor', 'q18_team_training_interest'
    ];

    for (const field of requiredFields) {
      if (!formData[field] && formData[field] !== 0) {
        toast.error(`Please complete all required fields`);
        return false;
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.q1_email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    // Validate ratings are 1-5
    const ratingFields = ['q5_course_objectives', 'q6_instructor_knowledge', 'q7_material_quality',
                          'q8_relevance', 'q9_understanding_improvement', 'q10_presentation_effectiveness'];
    for (const field of ratingFields) {
      const value = formData[field];
      if (value === null || value === undefined || value < 1 || value > 5) {
        toast.error('Please rate all course evaluation questions (1-5)');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    
    // Use XMLHttpRequest instead of fetch to avoid Response body consumption issues
    const token = localStorage.getItem('session_token');
    const xhr = new XMLHttpRequest();
    
    xhr.open('POST', `${API}/api/course-survey`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    
    xhr.onload = function() {
      setLoading(false);
      
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const responseData = JSON.parse(xhr.responseText);
          if (responseData.certificate_id) {
            setCertificateId(responseData.certificate_id);
          }
          
          // Check if certificate PDF is included in response
          if (responseData.certificate_pdf) {
            console.log('Certificate PDF received in response');
            const dataUrl = `data:application/pdf;base64,${responseData.certificate_pdf}`;
            setCertificatePdfUrl(dataUrl);
            
            toast.success('Survey completed! Opening your certificate...', {
              description: 'Your certificate is ready.'
            });
            
            // Show certificate modal immediately
            setTimeout(() => {
              setShowCertificate(true);
            }, 500);
          } else {
            console.log('No certificate PDF in response, attempting to fetch');
            toast.success('Survey completed! Loading your certificate...', {
              description: 'Your certificate will appear in a moment.'
            });
            
            // Fallback to fetching certificate
            setTimeout(() => {
              loadCertificate();
            }, 1000);
          }
        } catch (e) {
          console.error('Error parsing survey response:', e);
          toast.error('Survey submitted but certificate display failed');
        }
      } else {
        let errorMessage = 'Failed to submit survey';
        try {
          const errorData = JSON.parse(xhr.responseText);
          
          // Handle FastAPI validation errors (array of error objects)
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (errorData.detail) {
            errorMessage = JSON.stringify(errorData.detail);
          }
        } catch (e) {
          errorMessage = xhr.responseText || errorMessage;
        }
        
        console.error('Survey submission error:', errorMessage);
        toast.error(errorMessage);
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      toast.error('Error submitting survey. Please try again.');
    };
    
    xhr.send(JSON.stringify({
      ...formData,
      course_id: courseId,
      course_title: courseTitle
    }));
  };

  // Certificate Modal Overlay (shown over the survey form)
  const CertificateModal = () => {
    if (!showCertificate || !certificatePdfUrl) return null;
    
    return (
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]"
            onClick={() => {
              setShowCertificate(false);
              window.URL.revokeObjectURL(certificatePdfUrl);
              setCertificatePdfUrl(null);
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-2xl w-[90vw] max-w-4xl h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-xl font-bold" style={{ color: '#102862' }}>
                  Your CE Certificate
                </h2>
                <button
                  onClick={() => {
                    setShowCertificate(false);
                    window.URL.revokeObjectURL(certificatePdfUrl);
                    setCertificatePdfUrl(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* PDF Viewer */}
              <div className="flex-1 overflow-hidden">
                <iframe
                  src={certificatePdfUrl}
                  className="w-full h-full border-0"
                  title="CE Certificate"
                />
              </div>

              {/* Footer with actions */}
              <div className="px-6 py-4 border-t flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  Your certificate is also available in your dashboard under CE Certificates.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      // Download the certificate
                      const a = document.createElement('a');
                      a.href = certificatePdfUrl;
                      a.download = `certificate-${courseTitle.replace(/[^a-z0-9]/gi, '-')}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }}
                    variant="outline"
                  >
                    Download PDF
                  </Button>
                  <Button
                    onClick={() => navigate('/dashboard')}
                    style={{ background: '#102862' }}
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </div>
    );
  };

  return (
    <>
      <CertificateModal />
      
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #F4F4F4 0%, #DADEEO 100%)' }}>
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Card className="p-8">
          {/* Header */}
          <div className="mb-8 pb-6 border-b">
            <h1 className="text-3xl font-bold mb-2" style={{ color: '#092036' }}>
              Course Survey
            </h1>
            <p className="text-lg" style={{ color: '#102862' }}>
              Course: {courseTitle}
            </p>
            <p className="text-sm mt-2" style={{ color: '#70767C' }}>
              Your feedback is valuable and helps us improve our courses.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Section 1: About You */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b" style={{ color: '#092036' }}>
                About You
              </h2>

              {/* Q1: Email */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.q1_email}
                  onChange={(e) => handleInputChange('q1_email', e.target.value)}
                  placeholder="name@example.com"
                  className="w-full px-4 py-2 border rounded"
                  required
                />
              </div>

              {/* Q2: Profession */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Profession <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {[
                    'General Dentist',
                    'Dental Specialist (Oral Surgeon, Periodontist, etc.)',
                    'Dental Assistant',
                    'Dental Hygienist',
                    'Other'
                  ].map(option => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="profession"
                        value={option}
                        checked={formData.q2_profession === option}
                        onChange={(e) => handleInputChange('q2_profession', e.target.value)}
                        className="mr-3 w-4 h-4 cursor-pointer"
                        required
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ))}
                </div>
                {formData.q2_profession === 'Other' && (
                  <input
                    type="text"
                    value={formData.q2_profession_other}
                    onChange={(e) => handleInputChange('q2_profession_other', e.target.value)}
                    placeholder="Please specify"
                    className="w-full px-4 py-2 border rounded mt-2"
                  />
                )}
              </div>

              {/* Q3: Years Practicing */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Years practicing <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {['< 5 years', '5–10 years', '11–20 years', '21–30 years', '31+ years'].map(option => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="years"
                        value={option}
                        checked={formData.q3_years_practicing === option}
                        onChange={(e) => handleInputChange('q3_years_practicing', e.target.value)}
                        className="mr-3 w-4 h-4 cursor-pointer"
                        required
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Q4: Type of Practice */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Type of practice <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {[
                    'Solo private practice',
                    'Group private practice',
                    'Corporate practice',
                    'Academic',
                    'Institutional (e.g., Military, Correctional System, etc.)',
                    'Other'
                  ].map(option => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="practice"
                        value={option}
                        checked={formData.q4_practice_type === option}
                        onChange={(e) => handleInputChange('q4_practice_type', e.target.value)}
                        className="mr-3 w-4 h-4 cursor-pointer"
                        required
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ))}
                </div>
                {formData.q4_practice_type === 'Other' && (
                  <input
                    type="text"
                    value={formData.q4_practice_type_other}
                    onChange={(e) => handleInputChange('q4_practice_type_other', e.target.value)}
                    placeholder="Please specify"
                    className="w-full px-4 py-2 border rounded mt-2"
                  />
                )}
              </div>
            </div>

            {/* Section 2: Course Evaluation */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b" style={{ color: '#092036' }}>
                Course Evaluation
              </h2>
              <p className="text-xs mb-4 italic" style={{ color: '#70767C' }}>
                Circle one: 1 – Strongly Disagree   2   3   4   5 – Strongly Agree
              </p>

              {[
                { field: 'q5_course_objectives', text: 'The course objectives were clearly defined and consistent with the advertised description' },
                { field: 'q6_instructor_knowledge', text: 'The instructor was knowledgeable about the subject matter' },
                { field: 'q7_material_quality', text: 'The course material was current, well-organized, and presented at an appropriate level of detail' },
                { field: 'q8_relevance', text: 'The course content was relevant to my practice and will enhance my professional effectiveness' },
                { field: 'q9_understanding_improvement', text: 'The course improved my understanding of the topics covered (learning outcomes met)' },
                { field: 'q10_presentation_effectiveness', text: 'The instructor\'s presentation style and teaching methods were effective' }
              ].map(({ field, text }) => (
                <div key={field} className="mb-6 p-4 bg-gray-50 rounded">
                  <p className="text-sm font-medium mb-3" style={{ color: '#092036' }}>
                    {text} <span className="text-red-500">*</span>
                  </p>
                  <div className="flex gap-6 flex-wrap">
                    {[1, 2, 3, 4, 5].map(rating => (
                      <label key={rating} className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name={field}
                          value={rating}
                          checked={formData[field] === rating}
                          onChange={() => handleInputChange(field, rating)}
                          className="mr-2 w-4 h-4 cursor-pointer"
                          required
                        />
                        <span className="text-sm font-medium">{rating}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-between text-xs mt-2" style={{ color: '#70767C' }}>
                    <span>1 - Strongly Disagree</span>
                    <span>5 - Strongly Agree</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Section 3: Future Courses and Preferences */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b" style={{ color: '#092036' }}>
                Future Courses and Preferences
              </h2>

              {/* Q11: Topics of Interest */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Topics of interest for future courses: <span className="text-xs">(Check all that apply)</span>
                </label>
                <div className="space-y-2">
                  {[
                    { field: 'q11_dentoalveolar_surgery', label: 'Dentoalveolar surgery' },
                    { field: 'q11_third_molar_surgery', label: 'Third molar surgery' },
                    { field: 'q11_oral_pathology', label: 'Oral pathology' },
                    { field: 'q11_dental_implants', label: 'Dental implants' },
                    { field: 'q11_surgical_complications', label: 'Surgical complications' },
                    { field: 'q11_bone_grafting', label: 'Bone grafting' },
                    { field: 'q11_sedation_anesthesia', label: 'Sedation/anesthesia' }
                  ].map(({ field, label }) => (
                    <label key={field} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData[field]}
                        onChange={(e) => handleInputChange(field, e.target.checked)}
                        className="mr-3 w-4 h-4 cursor-pointer"
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Q12: Preferred Content Level */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Preferred level of content: <span className="text-xs">(Check one)</span> <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {['Introductory', 'Intermediate', 'Advanced'].map(option => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="content_level"
                        value={option}
                        checked={formData.q12_content_level === option}
                        onChange={(e) => handleInputChange('q12_content_level', e.target.value)}
                        className="mr-3 w-4 h-4 cursor-pointer"
                        required
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Q13: Preferred Day */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Preferred day for courses: <span className="text-xs">(Check one)</span> <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {['Weekdays', 'Weekends (Saturday/Sunday only)', 'Weekends (includes Friday)'].map(option => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="preferred_day"
                        value={option}
                        checked={formData.q13_preferred_day === option}
                        onChange={(e) => handleInputChange('q13_preferred_day', e.target.value)}
                        className="mr-3 w-4 h-4 cursor-pointer"
                        required
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Q14: Preferred Time */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Preferred time of day: <span className="text-xs">(Check one)</span> <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {['Morning', 'Afternoon', 'Evening'].map(option => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="preferred_time"
                        value={option}
                        checked={formData.q14_preferred_time === option}
                        onChange={(e) => handleInputChange('q14_preferred_time', e.target.value)}
                        className="mr-3 w-4 h-4 cursor-pointer"
                        required
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Q15: Preferred Learning Format */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Preferred learning format: <span className="text-xs">(Check one)</span> <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {[
                    'Lecture (In person)',
                    'Hands-on workshop',
                    'Online/webinar',
                    'Cohort (study group)',
                    'Self-paced (on demand)'
                  ].map(option => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="learning_format"
                        value={option}
                        checked={formData.q15_learning_format === option}
                        onChange={(e) => handleInputChange('q15_learning_format', e.target.value)}
                        className="mr-3 w-4 h-4 cursor-pointer"
                        required
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Q16-Q18: Yes/No Questions */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Are you interested in networking opportunities at courses? <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  {['Yes', 'No'].map(option => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="networking"
                        value={option}
                        checked={formData.q16_networking_interest === option}
                        onChange={(e) => handleInputChange('q16_networking_interest', e.target.value)}
                        className="mr-2 w-4 h-4 cursor-pointer"
                        required
                      />
                      <span className="text-sm font-medium">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Was cost a significant factor in your decision to attend? <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  {['Yes', 'No'].map(option => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="cost"
                        value={option}
                        checked={formData.q17_cost_factor === option}
                        onChange={(e) => handleInputChange('q17_cost_factor', e.target.value)}
                        className="mr-2 w-4 h-4 cursor-pointer"
                        required
                      />
                      <span className="text-sm font-medium">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Are you interested in team training (courses that include the entire team)? <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  {['Yes', 'No'].map(option => (
                    <label key={option} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="team_training"
                        value={option}
                        checked={formData.q18_team_training_interest === option}
                        onChange={(e) => handleInputChange('q18_team_training_interest', e.target.value)}
                        className="mr-2 w-4 h-4 cursor-pointer"
                        required
                      />
                      <span className="text-sm font-medium">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Section 4: Open Feedback */}
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 pb-2 border-b" style={{ color: '#092036' }}>
                Open Feedback
              </h2>

              {/* Q19 */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  What was the most valuable aspect of this course?
                </label>
                <textarea
                  value={formData.q19_most_valuable_aspect}
                  onChange={(e) => handleInputChange('q19_most_valuable_aspect', e.target.value)}
                  rows="4"
                  className="w-full px-4 py-2 border rounded"
                  placeholder="Your answer..."
                />
              </div>

              {/* Q20 */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  What changes or improvements would you suggest for the course?
                </label>
                <textarea
                  value={formData.q20_suggested_changes}
                  onChange={(e) => handleInputChange('q20_suggested_changes', e.target.value)}
                  rows="4"
                  className="w-full px-4 py-2 border rounded"
                  placeholder="Your suggestions..."
                />
              </div>

              {/* Q21 */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#092036' }}>
                  Do you have any other feedback or comments?
                </label>
                <textarea
                  value={formData.q21_other_feedback}
                  onChange={(e) => handleInputChange('q21_other_feedback', e.target.value)}
                  rows="4"
                  className="w-full px-4 py-2 border rounded"
                  placeholder="Additional comments..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6 border-t">
              <Button
                type="submit"
                disabled={loading}
                className="text-white"
                style={{ background: '#102862' }}
              >
                {loading ? 'Submitting...' : 'Submit Survey'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
    </>
  );
}
